'use client';

import { useOutsideClick } from '@/hooks/useOutsideClick';
import type { GroupField, Ordering } from '@prismicio/client';
import { memo, useState } from 'react';

import { ArrowDown } from '@/assets/icons/ArrowDown';
import type {
  BlogDocumentDataSortByItem,
  Simplify,
} from '../../../prismicio-types';

import { CheckIcon } from './assets/CheckIcon';
import styles from './sort.module.scss';

interface SortProps {
  sortList: GroupField<Simplify<BlogDocumentDataSortByItem>>;
  sortArticles: (sortBy: Array<Ordering>) => void;
}

type Keys = 'Latest' | 'Newest';

type Option = {
  [key in Keys]: {
    orderings: Array<Ordering>;
  };
};
const options: Option = {
  Latest: {
    orderings: [{ field: 'my.blog_article.date' }],
  },
  Newest: {
    orderings: [{ field: 'my.blog_article.date', direction: 'desc' }],
  },
};

const SortComponent = ({ sortList, sortArticles }: SortProps) => {
  const [sortBy, setSortBy] = useState<{
    name: Keys;
    orderings: Array<Ordering>;
  } | null>(null);
  const [currentItem, setCurrentItem] = useState<Keys | null>(null);
  const [showSortOptions, setShowSortOptions] = useState(false);

  const sortRef = useOutsideClick<HTMLDivElement>(() =>
    setShowSortOptions(false)
  );

  const handleOnClickOption = (key: Keys) => {
    setSortBy({ name: key, orderings: options[key].orderings });
    sortArticles(options[key].orderings);
    setShowSortOptions(false);
  };

  const handleMouseEvent = (
    event: React.MouseEvent<HTMLButtonElement>,
    key: Keys
  ) => {
    if (event.type === 'mouseenter') {
      setCurrentItem(key);
    } else {
      setCurrentItem(sortBy ? sortBy.name : null);
    }
  };

  return (
    <div className={styles.sort} ref={sortRef}>
      <button
        type="button"
        className={`${styles.current_option} ${showSortOptions ? styles.isOpen : ''}`}
        onClick={() => setShowSortOptions((prev) => !prev)}
      >
        <span className={currentItem ? '' : styles.opacity}>
          {currentItem || 'Sort by'}
        </span>
        <div
          className={`${styles.icon_wrapper} ${showSortOptions ? styles.openIcon : ''}`}
        >
          <ArrowDown />
        </div>
      </button>
      {showSortOptions && (
        <div className={styles.options_menu}>
          {sortList.map((option, index) => (
            <button
              type="button"
              className={styles.option_button}
              onClick={() => handleOnClickOption(option.item)}
              key={index}
              onMouseEnter={(event) => handleMouseEvent(event, option.item)}
              onMouseLeave={(event) => handleMouseEvent(event, option.item)}
            >
              <span>{option.item}</span>
              <CheckIcon />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const Sort = memo(SortComponent);
